"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ColumnDef } from "@tanstack/react-table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { getInitials } from "@/lib/utils";
import { DataPendaftaran } from "@/types";
import { ArrowUpDown, Activity } from "lucide-react";
import { Link } from "react-router-dom";

export const columns: ColumnDef<DataPendaftaran>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <div className="px-1">
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="border-slate-300 dark:border-slate-700 data-[state=checked]:bg-primary"
        />
      </div>
    ),
    cell: ({ row }) => (
      <div className="px-1">
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="border-slate-300 dark:border-slate-700 data-[state=checked]:bg-primary"
        />
      </div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "verifikasi",
    header: () => (
      <div className="text-center font-semibold text-slate-600 dark:text-slate-300">
        Aksi
      </div>
    ),
    cell: ({ row }) => {
      const nomorPendaftaran = row.original?.nomor_pendaftaran;

      return (
        <div className="flex justify-center">
          <Link
            target="_blank"
            to={`/verifikator/surat-keterangan-sehat/verifikasi-peserta-surat-keterangan-sehat/view?no=${nomorPendaftaran}`}
          >
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-3 shadow-sm hover:bg-primary hover:text-white transition-all dark:border-slate-700 dark:bg-slate-900"
            >
              <Activity className="w-3.5 h-3.5 mr-1.5" />
              Verifikasi
            </Button>
          </Link>
        </div>
      );
    },
  },
  {
    accessorKey: "is_validated",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Status Validasi
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const dataPendaftaran = row.original;
      const berkasSuratKeteranganSehat = dataPendaftaran?.data_berkas?.find(
        (item) =>
          item.sys_berkas?.jenis_berkas?.slug_jenis_berkas ===
          "Surat_Keterangan_Sehat",
      );

      const isValid = berkasSuratKeteranganSehat?.is_valid;

      if (isValid === null || isValid === undefined) {
        return (
          <Badge
            variant="outline"
            className="bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400 font-medium whitespace-nowrap"
          >
            Menunggu Validasi
          </Badge>
        );
      }

      return isValid ? (
        <Badge
          variant="outline"
          className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400 font-medium whitespace-nowrap"
        >
          Valid
        </Badge>
      ) : (
        <Badge
          variant="outline"
          className="bg-destructive/10 text-destructive border-destructive/20 dark:text-red-400 font-medium whitespace-nowrap"
        >
          Tidak Valid
        </Badge>
      );
    },
  },

    {
    accessorKey: "catatan_verifikator",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Catatan Verifikator
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => {
          const berkasSuratKeteranganSehat = row.original?.data_berkas?.find((berkas)=>{
        return berkas.sys_berkas?.jenis_berkas?.slug_jenis_berkas == "Surat_Keterangan_Sehat"
      })
 
        return <div>
     <h1 className="font-semibold">

         {berkasSuratKeteranganSehat?.user_verifikator?.nama_lengkap}
          </h1>
          <p className="italic">

          {berkasSuratKeteranganSehat?.catatan_verifikator}
          </p>
      </div>
    },
  },
  {
    accessorKey: "data_pendaftaran.nomor_pendaftaran",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          No. Pendaftaran
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => {
      return (
        <div className="font-mono text-sm text-slate-700 dark:text-slate-300">
          {row.original?.nomor_pendaftaran ?? "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "data_siswa.nama_lengkap",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Nama Lengkap
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const nama = row.original?.data_siswa?.nama_lengkap || "";
      return (
        <div
          className="font-medium text-slate-900 dark:text-slate-100 capitalize truncate max-w-[200px]"
          title={nama}
        >
          {nama || "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "unitkerja.nama",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Program Studi
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const prodi = row.original?.unitkerja?.nama;
      return (
        <div className="text-sm truncate max-w-[180px]" title={prodi}>
          {prodi || "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "data_siswa.email",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Email
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const email = row.original?.data_siswa?.email || "";
      return (
        <div
          className="text-xs text-muted-foreground truncate max-w-[180px]"
          title={email}
        >
          {email || "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "data_siswa.handphone",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          No Handphone
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => {
      return (
        <div className="text-sm text-muted-foreground">
          {row.original?.data_siswa?.handphone || "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "data_siswa.jenis_kelamin",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 font-semibold text-slate-600 dark:text-slate-300"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          L/P
          <ArrowUpDown className="w-3.5 h-3.5 ml-2 opacity-50" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const jk = row.original?.data_siswa?.jenis_kelamin as string | null;
      return (
        <div className="text-center font-medium text-slate-500">
          {jk === "Laki-laki" || jk === "L"
            ? "L"
            : jk === "Perempuan" || jk === "P"
              ? "P"
              : "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "data_siswa.path_foto",
    header: () => (
      <div className="text-center font-semibold text-slate-600 dark:text-slate-300">
        Foto
      </div>
    ),
    cell: ({ row }) => {
      const nama = row.original?.data_siswa?.nama_lengkap;
      const foto = row.original?.data_siswa?.path_foto;
      return (
        <div className="flex justify-center">
          <Avatar className="h-9 w-9 border border-slate-200 dark:border-slate-800 shadow-sm">
            <AvatarImage
              src={foto || ""}
              alt={nama || "Foto"}
              className="object-cover"
            />
            <AvatarFallback className="text-[10px] bg-slate-100 dark:bg-slate-800 font-medium">
              {getInitials(nama || "A")}
            </AvatarFallback>
          </Avatar>
        </div>
      );
    },
    enableSorting: false,
  },
];
